import { useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Field } from 'react-final-form';
import cn from 'classnames';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Row } from 'UIPackage/FormModule/components/Row';
import {
  IKnmErknmFormViolation,
  IViolationMaterialTypeItem,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { IViolationMaterialTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IViolationMaterialTypeSelectOption';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './ErknmDecisionViolationFixPlanMethodsField.module.scss';

interface IErknmDecisionViolationFixPlanMethodsFieldProps {
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  violation: IKnmErknmFormViolation;
  watchOnly: boolean;
  isPaperless: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  mId: string;
  isResultDecisionSinged?: boolean;
  extractVersion?: number;
}

export const ErknmDecisionViolationFixPlanMethodsField = ({
  fieldName,
  formRef,
  violation,
  watchOnly,
  isPaperless,
  mId,
  isKnm,
  isPm,
  isDecision,
  isResultDecisionSinged,
  extractVersion,
}: IErknmDecisionViolationFixPlanMethodsFieldProps) => {
  const methodsFieldName = `${fieldName}.fixPlanMethods`;
  const methodsRequiredFieldName = `${fieldName}.fixPlanMethodsRequired`;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isOnApproval: boolean = violation.status === ViolationStatusesEnum.APPROVING;

  const isNotFixedByStatus: boolean = violation.status === ViolationStatusesEnum.NOT_FIXED;

  const transformSelectOptions = useCallback(
    (arr: IViolationMaterialTypeSelectOption[] | undefined) =>
      arr?.filter(option => !violation.fixPlanMethods?.map(item => item.value?.value).includes(option.value)),
    [violation],
  );

  const handleAdd = useCallback(() => {
    const newMethodItem: IViolationMaterialTypeItem = {
      isNew: true,
      value: null,
    };

    push && push(methodsFieldName, newMethodItem);
  }, []);
  const handleRemove = useCallback(
    (idx: number) => () => {
      remove && remove(methodsFieldName, idx);
    },
    [],
  );

  return (
    <>
      <FieldBlock
        id={`${mId}.fixPlanMethodsRequired`}
        label={getTitleFieldWithRedStar({
          label: 'Планируемый способ устранения нарушения',
          field: methodsRequiredFieldName,
          isPm,
          isKnm,
          isDecision,
        })}
        color={isPaperless ? undefined : 'black'}
        labelFontSize={isPaperless ? undefined : 'small'}
        className={cn({ [styles.GroupMarginTop]: isPaperless })}
        button={
          (isNotFixedByStatus || !violation.status) && !isOnApproval && !watchOnly && !violation.fixes?.length ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={handleAdd}
            />
          ) : undefined
        }
      />
      <FieldArray name={methodsFieldName}>
        {({ fields }) => {
          return (
            <>
              {fields.map((methodFieldName, idx) => {
                const methodValue = fields.value[idx];

                const isSecondSign = typeof extractVersion === 'number' && extractVersion > 0;

                const isDisabled =
                  isResultDecisionSinged !== undefined
                    ? (isResultDecisionSinged || isSecondSign) && !methodValue.isNew
                    : !methodValue.isNew;

                return (
                  <div className={styles.RowWrapper} key={methodFieldName}>
                    <Row notSpace={!isPaperless}>
                      <FieldBlock id={`${mId}.fixPlanMethods[${idx}].value`}>
                        <SelectInputFieldAutoOptions
                          id={`${methodFieldName}.value`}
                          name={`${methodFieldName}.value`}
                          optionName={CommonCatalogsNamesEnum.VIOLATION_MATERIAL_TYPE}
                          isClearable={false}
                          placeholder="Выберите способ"
                          validate={makeRequired('Поле не может быть пустым')}
                          disabled={isDisabled}
                          transformFunc={transformSelectOptions}
                        />
                      </FieldBlock>
                      {!isDisabled && <CloseButton className="shared-deleting-row-btn" onClick={handleRemove(idx)} />}
                    </Row>
                  </div>
                );
              })}
              <Field name={methodsRequiredFieldName}>
                {({ meta }) => {
                  if (typeof meta.error === 'string' && meta.touched) {
                    return <p className="shared-input-error shared-row__small-margin">{meta.error}</p>;
                  }
                }}
              </Field>
            </>
          );
        }}
      </FieldArray>
    </>
  );
};
